import type { ResidentDefaultFormValues } from '../types'

/** Resident lifecycle (`resident` table) — must use `page=resident`. */
const RESIDENT_LIFECYCLE_STATUSES = new Set(['NOT_STARTED', 'ACTIVE', 'CLOSED'])

const RESIDENT_LIFECYCLE_PROGRESS = new Set([
  'LEASE_PENDING',
  'NOT_MOVED_IN',
  'APPLICANT_APPROVED',
  'CURRENT_RESIDENT',
  'NORMAL_END',
  'EARLY_END',
])

/** Reimbursement workflow (`reimbursement` table) — `page=default`. */
const REIMBURSEMENT_WORKFLOW_STATUSES = new Set(['OPEN', 'SUBMITTED', 'CONCLUDED'])

const REIMBURSEMENT_WORKFLOW_PROGRESS = new Set([
  'INFO_NEEDED',
  'ACTION_NEEDED',
  'AWAITING_FINAL_INFO',
  'READY_FOR_REVIEW',
  'IN_REVIEW',
  'DENIED',
  'PAID',
  'CANCELLED',
  'EXPIRED',
])

export function isResidentLifecycleRentalStatus(status: unknown): boolean {
  const normalized = String(status ?? '').trim()
  return normalized !== '' && RESIDENT_LIFECYCLE_STATUSES.has(normalized)
}

export function isResidentLifecycleRentalProgress(progress: unknown): boolean {
  const normalized = String(progress ?? '').trim()
  return normalized !== '' && RESIDENT_LIFECYCLE_PROGRESS.has(normalized)
}

export function isReimbursementWorkflowRentalStatus(status: unknown): boolean {
  const normalized = String(status ?? '').trim()
  return normalized !== '' && REIMBURSEMENT_WORKFLOW_STATUSES.has(normalized)
}

export function isReimbursementWorkflowRentalProgress(progress: unknown): boolean {
  const normalized = String(progress ?? '').trim()
  return normalized !== '' && REIMBURSEMENT_WORKFLOW_PROGRESS.has(normalized)
}

type RentalFieldSource = Pick<
  ResidentDefaultFormValues,
  | 'rental_status'
  | 'rental_progress'
  | 'reimbursement_rental_status'
  | 'reimbursement_rental_progress'
>

export function resolveReimbursementRentalFieldsForApi(
  source: RentalFieldSource,
): { rental_status?: string; rental_progress?: string } {
  const dedicatedStatus = String(source.reimbursement_rental_status ?? '').trim()
  const sharedStatus = String(source.rental_status ?? '').trim()
  const rental_status =
    dedicatedStatus ||
    (isReimbursementWorkflowRentalStatus(sharedStatus) ? sharedStatus : '')

  const dedicatedProgress = String(source.reimbursement_rental_progress ?? '').trim()
  const sharedProgress = String(source.rental_progress ?? '').trim()
  let rental_progress = dedicatedProgress
  if (!rental_progress && rental_status) {
    if (isReimbursementWorkflowRentalProgress(sharedProgress)) {
      rental_progress = sharedProgress
    } else if (!isResidentLifecycleRentalProgress(sharedProgress)) {
      rental_progress = sharedProgress
    }
  }

  return {
    ...(rental_status ? { rental_status } : {}),
    ...(rental_progress ? { rental_progress } : {}),
  }
}

export function resolveResidentRentalFieldsForApi(
  source: RentalFieldSource,
): { rental_status?: string; rental_progress?: string } {
  const sharedStatus = String(source.rental_status ?? '').trim()
  const rental_status = isResidentLifecycleRentalStatus(sharedStatus) ? sharedStatus : ''

  const sharedProgress = String(source.rental_progress ?? '').trim()
  const rental_progress =
    rental_status && isResidentLifecycleRentalProgress(sharedProgress) ? sharedProgress : ''

  return {
    ...(rental_status ? { rental_status } : {}),
    ...(rental_progress ? { rental_progress } : {}),
  }
}

/** Keys that belong on reimbursement (`page=default`) saves only. */
const REIMBURSEMENT_ONLY_PAYLOAD_KEYS = new Set([
  'reimbursement_type',
  'reimbursement_rental_status',
  'reimbursement_rental_progress',
  'rental_status_date',
  'first_month_not_paid_month',
  'first_month_not_paid_year',
  'first_month_not_paid',
  'adjusted_notice',
  'adjusted_reason',
  'adjust_max_movein_date',
  'movein_date',
  'new_resident_lease_start_date',
  'moveout_date',
  'is_final_ledger',
  'skip_claim_affidavit_required',
  'lease_agreement_status',
  'payment_ledger_status',
  'skip_claim_affidavit_status',
  'unlawful_detainer_status',
  'date_unit_is_back_on_market',
  'unit_vacancy',
  'cdc_declaration',
  'payment_plan',
  'cdc_affidavit',
  'late_letter',
  'property_unused_security_deposit',
  'unused_security_deposit',
  'security_deposit_change_reasons',
  'partial_payment_amount',
  'loss_adjustment',
  'loss_adjusted_reason',
  'net_amount',
  'timely',
  'claim_expiration_date',
])

/** Keys that belong on resident lifecycle (`page=resident`) saves only. */
const RESIDENT_ONLY_PAYLOAD_KEYS = new Set(['unit_number', 'lease_start_date', 'lease_end_date', 'lease_verified'])

function applyReimbursementRentalFieldsToPayload(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
): void {
  delete payload.reimbursement_rental_status
  delete payload.reimbursement_rental_progress
  const rental = resolveReimbursementRentalFieldsForApi(values)
  if (rental.rental_status) payload.rental_status = rental.rental_status
  else delete payload.rental_status
  if (rental.rental_progress) payload.rental_progress = rental.rental_progress
  else delete payload.rental_progress
}

function applyResidentRentalFieldsToPayload(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
): void {
  delete payload.reimbursement_rental_status
  delete payload.reimbursement_rental_progress
  delete payload.reimbursement_type
  const rental = resolveResidentRentalFieldsForApi(values)
  if (rental.rental_status) payload.rental_status = rental.rental_status
  else delete payload.rental_status
  if (rental.rental_progress) payload.rental_progress = rental.rental_progress
  else delete payload.rental_progress
}

export function scopePayloadForReimbursementPage(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
): void {
  for (const key of RESIDENT_ONLY_PAYLOAD_KEYS) {
    delete payload[key]
  }
  applyReimbursementRentalFieldsToPayload(payload, values)
}

/** Resident Information tab — lifecycle fields only, no reimbursement claim scalars. */
export function scopePayloadForResidentInfoTab(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
): void {
  for (const key of REIMBURSEMENT_ONLY_PAYLOAD_KEYS) {
    delete payload[key]
  }
  applyResidentRentalFieldsToPayload(payload, values)
}

/** `page=resident` from Calculation / Liberty Internal — fix rental keys only. */
export function scopePayloadForResidentWorkflowPage(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
): void {
  applyResidentRentalFieldsToPayload(payload, values)
}

export function scopePayloadByApiPage(
  payload: Record<string, unknown>,
  values: RentalFieldSource,
  page: 'resident' | 'default',
  options?: { residentInfoTab?: boolean },
): void {
  if (page === 'default') {
    scopePayloadForReimbursementPage(payload, values)
    return
  }
  if (options?.residentInfoTab) {
    scopePayloadForResidentInfoTab(payload, values)
    return
  }
  scopePayloadForResidentWorkflowPage(payload, values)
}
